import { API_BASE_URL } from '../env';
import { getAuthHeaders } from '../session/clientSession';
import { emitLoadingEnd, emitLoadingProgress, emitLoadingStart } from '../../common/loading/loadingEvents';

export class HttpError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

async function parseBody(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => ({}));
  }
  return response.text().catch(() => '');
}

export async function request(path, {
  method = 'GET',
  headers = {},
  body,
  json,
  signal,
  timeoutMs,
  estimatedDurationMs,
  loadingKey,
  loadingText,
  showPercentage = true,
  baseUrl = API_BASE_URL,
} = {}) {
  const key = loadingKey || path;
  emitLoadingStart(key, { text: loadingText, showPercentage });
  const progressStartedAt = Date.now();
  const progressTimer = estimatedDurationMs
    ? setInterval(() => {
      const elapsed = Date.now() - progressStartedAt;
      emitLoadingProgress(key, Math.min(90, Math.round((elapsed / estimatedDurationMs) * 90)));
    }, 250)
    : null;
  // A stalled request must abort rather than hang: the loading screen only
  // clears once this call settles, so racing a timer would leave it up forever.
  const controller = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timedOut = false;
  let timeoutId;
  if (controller) {
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
  try {
    const payload = json !== undefined ? JSON.stringify(json) : body;
    const response = await fetch(`${baseUrl}${path.startsWith('/') ? path : `/${path}`}`, {
      method,
      signal: controller ? controller.signal : signal,
      headers: {
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...getAuthHeaders(),
        ...headers,
      },
      body: payload,
    });
    // Stop estimating before parsing, otherwise a pending tick can push the
    // bar back below 100% while a large body is still being read.
    if (progressTimer) clearInterval(progressTimer);
    emitLoadingProgress(key, 100);
    const data = await parseBody(response);
    if (!response.ok) {
      const message = (data && data.error) || (typeof data === 'string' && data) || `HTTP ${response.status}`;
      throw new HttpError(message, { status: response.status, data });
    }
    return data;
  } catch (error) {
    if (timedOut) {
      throw new HttpError(`Request timed out after ${timeoutMs}ms`, { status: 0, data: { timeout: true } });
    }
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    if (progressTimer) clearInterval(progressTimer);
    emitLoadingEnd(key);
  }
}

export const httpGet = (path, options) => request(path, { ...options, method: 'GET' });
export const httpPost = (path, json, options) => request(path, { ...options, method: 'POST', json });
export const httpPut = (path, json, options) => request(path, { ...options, method: 'PUT', json });
export const httpDelete = (path, options) => request(path, { ...options, method: 'DELETE' });
